/**
 * Servicio para obtener los banners publicitarios del ticker de Clarosports.
 *
 * El endpoint devuelve un arreglo de banners. Cada banner es un objeto con una
 * única clave (bannerN) cuyo valor es un arreglo con la configuración:
 *
 * [
 *   {
 *     "banner1": [
 *       {
 *         "duration": 50,               // segundos que dura cada imagen del banner
 *         "move": "expandir hacia arriba", // comportamiento visual del banner
 *         "imagenes": [                  // imágenes que rotan dentro del banner
 *           { "url": "", "name": "" },
 *           { "url": "", "name": "" }
 *         ]
 *       }
 *     ]
 *   },
 *   ...
 * ]
 */

import axios from 'axios';
import { API_CORE_URL_RELATIVE } from "../constantes/constantes";

const BASE_TICKER = "/api/core-clarosports/v2/ticker";

/**
 * Bandera de pruebas locales.
 *
 * Cuando es `true`, el servicio lee un JSON de banners de prueba servido
 * estáticamente desde `public/data/tickerBanners.json` en lugar de llamar al
 * backend. Útil para validar los comportamientos (fijo, expandir izquierda,
 * expandir arriba) sin depender del servicio real.
 *
 * También se puede activar en tiempo de ejecución desde la consola del
 * navegador con: window.useMockTickerBanners = true
 */
const USE_MOCK_BANNERS = false;

/**
 * Ruta del JSON de prueba (relativa al `homepage` "/results" del build de CRA).
 * `process.env.PUBLIC_URL` resuelve la ruta correcta tanto en dev como en prod.
 */
const MOCK_BANNERS_URL = `${process.env.PUBLIC_URL || ""}/data/tickerBanners.json`;

function isMockEnabled() {
  if (typeof window !== "undefined" && typeof window.useMockTickerBanners === "boolean") {
    return window.useMockTickerBanners;
  }
  return USE_MOCK_BANNERS;
}

/**
 * Obtiene la configuración de banners publicitarios del ticker.
 *
 * @async
 * @returns {Promise<{statusCode: number, data: Array, message?: string}>}
 */
export async function getTickerBanners() {
  let endpoint = isMockEnabled()
    ? MOCK_BANNERS_URL
    : `${API_CORE_URL_RELATIVE}${BASE_TICKER}/banners`;

  try {
    const { data, status } = await axios.get(endpoint, {
      timeout: 25000,
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json; charset=utf-8',
      },
    });

    return { statusCode: status, data };
  } catch (error) {
    console.error('Error en la petición getTickerBanners:', error.message || error);

    const status = error.response?.status || 500;
    const message = error.response?.data?.message || 'Error al obtener los banners del ticker';

    return { statusCode: status, data: [], message };
  }
}
